/** Startet den Optimierungen-Drawer von der Optimierungen-Liste aus.
 * Löst pro Produkt den Channel mit den meisten Text-Verstößen auf und mappt
 * UUID→JTL-SalesChannelId (für /push). Nicht-JTL-Channels werden ausgelassen.
 */
import { useCallback, useState } from 'react'

import { getChannels } from '@/lib/api/channels'
import { getProductReadiness } from '@/lib/api/channel-readiness'

import { buildOptimierungFields } from './buildFields'
import type { OptimierungenDrawerProps } from './OptimierungenDrawer'

export interface LaunchTarget {
  product_id: string
  name?: string | null
}

export function useOptimierungenLauncher(creditBalance?: string) {
  const [props, setProps] = useState<OptimierungenDrawerProps | null>(null)

  const launch = useCallback(async (target: LaunchTarget) => {
    const [readiness, channelsRes] = await Promise.all([
      getProductReadiness(target.product_id),
      getChannels(true),
    ])
    const ranked = (readiness.scores ?? [])
      .map((s) => ({ score: s, fields: buildOptimierungFields(s.issues) }))
      .filter((x) => x.fields.length > 0)
      .sort((a, b) => b.fields.length - a.fields.length)
    if (!ranked.length) return

    const { score, fields } = ranked[0]
    const ch = channelsRes.channels.find((c) => c.id === score.channel_id)
    if (!ch?.channel_id) return // Nicht-JTL / unbekannt → kein Push-Pfad

    setProps({
      open: true,
      productId: target.product_id,
      productName: target.name ?? '',
      channelId: score.channel_id,
      salesChannelId: ch.channel_id,
      channelName: ch.custom_name || ch.channel_name,
      channelHost: ch.integration_name || undefined,
      channelLanguage: 'DE',
      creditBalance,
      initialFields: fields,
      onClose: () => setProps(null),
    })
  }, [creditBalance])

  return { props, launch }
}
